import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { 
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { GET_PRODUCT, GET_PRODUCTS, GET_PRODUCT_STATS, GET_CATEGORIES } from '../graphql/product-queries'
import {
  UPDATE_PRODUCT,
  ASSIGN_CATEGORY_TO_PRODUCT,
  REMOVE_CATEGORY_FROM_PRODUCT,
} from '../graphql/product-mutations'
import { UpdateProductData, Product, PlatformAvailability, Category } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import PlatformPicker from '../components/PlatformPicker'
import CategoryAssignmentPicker from '../components/CategoryAssignmentPicker'
import AllergenAttributeEditor, { AllergenAttributeValue } from '../components/AllergenAttributeEditor'
import toast from 'react-hot-toast'

const EditProductPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const client = useApolloClient()
  const [formData, setFormData] = useState<UpdateProductData>({
    name: '',
    kind: 'BASE',
    description: '',
    image_url: '',
    active: true,
    display_in_pos: true,
    display_in_webshop: true,
  })
  const [enabledSlugs, setEnabledSlugs] = useState<Set<string>>(new Set())
  const [inFlightCategoryIds, setInFlightCategoryIds] = useState<Set<number>>(new Set())
  const [labels, setLabels] = useState<AllergenAttributeValue>({ allergens: [], attributes: [] })

  const handlePlatformChange = (slug: string, enabled: boolean) => {
    setEnabledSlugs(prev => {
      const next = new Set(prev)
      if (enabled) next.add(slug)
      else next.delete(slug)
      return next
    })
  }

  const { data: productData, loading: productLoading, error: productError } = useQuery(GET_PRODUCT, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const { data: categoriesData } = useQuery(GET_CATEGORIES, {
    variables: { filters: {} },
  })

  const [assignCategory] = useMutation(ASSIGN_CATEGORY_TO_PRODUCT, {
    refetchQueries: [
      { query: GET_PRODUCT, variables: { id: parseInt(id!) } },
    ],
    awaitRefetchQueries: true,
  })

  const [removeCategory] = useMutation(REMOVE_CATEGORY_FROM_PRODUCT, {
    refetchQueries: [
      { query: GET_PRODUCT, variables: { id: parseInt(id!) } },
    ],
    awaitRefetchQueries: true,
  })

  const handleCategoryChange = async (categoryId: number, assigned: boolean) => {
    if (!id) return
    const productId = parseInt(id)
    setInFlightCategoryIds(prev => new Set(prev).add(categoryId))
    try {
      if (assigned) {
        await assignCategory({ variables: { productId, categoryId } })
      } else {
        await removeCategory({ variables: { productId, categoryId } })
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update category assignment')
    } finally {
      setInFlightCategoryIds(prev => {
        const next = new Set(prev)
        next.delete(categoryId)
        return next
      })
    }
  }

  const [updateProduct, { loading: updateLoading }] = useMutation(UPDATE_PRODUCT, {
    onCompleted: (data) => {
      toast.success('Product updated successfully!')
      navigate(`/products/${data.updateProduct.id}`)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update product')
    },
    update: (cache, { data }) => {
      if (data?.updateProduct) {
        // Update the products list cache
        const existingProducts: any = cache.readQuery({
          query: GET_PRODUCTS,
          variables: { filters: {} }
        })

        if (existingProducts) {
          const updatedProducts = existingProducts.products.map((product: any) =>
            product.id === data.updateProduct.id ? data.updateProduct : product
          )
          
          cache.writeQuery({
            query: GET_PRODUCTS,
            variables: { filters: {} },
            data: {
              products: updatedProducts
            }
          })
        }

        // For stats, we'll refetch to be safe since we don't know the previous state
        client.refetchQueries({
          include: [GET_PRODUCT_STATS]
        })
      }
    }
  })

  // Populate form when product data loads
  useEffect(() => {
    if (productData?.product) {
      const product: Product = productData.product
      setFormData({
        name: product.name,
        kind: product.kind,
        description: product.description || '',
        image_url: product.image_url || '',
        active: product.active,
        display_in_pos: product.display_in_pos,
        display_in_webshop: product.display_in_webshop,
      })
      const initial = new Set<string>(
        (product.platforms ?? [])
          .filter((a: PlatformAvailability) => a.enabled)
          .map((a: PlatformAvailability) => a.platform.slug)
      )
      setEnabledSlugs(initial)
      setLabels({
        allergens: product.allergen_info_json?.allergens ?? [],
        attributes: product.attributes_json?.attributes ?? [],
      })
    }
  }, [productData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name?.trim()) {
      toast.error('Product name is required')
      return
    }

    updateProduct({
      variables: {
        id: parseInt(id!),
        productData: {
          ...formData,
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          image_url: formData.image_url?.trim() || undefined,
          platformSlugs: Array.from(enabledSlugs),
          // Full-array overwrite (API replaces, does not merge). Form was seeded
          // from the current values, so this is a safe whole-object write; [] clears.
          allergen_info_json: { allergens: labels.allergens },
          attributes_json: { attributes: labels.attributes },
        }
      }
    })
  }

  const handleInputChange = (field: keyof UpdateProductData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (productLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (productError || !productData?.product) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❌</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Product not found</h3>
        <p className="text-gray-500 mb-6">The product you're looking for doesn't exist.</p>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    )
  }

  const product: Product = productData.product

  const availabilities: PlatformAvailability[] = (product.platforms ?? []).map(
    (a: PlatformAvailability) => ({
      platform: a.platform,
      enabled: enabledSlugs.has(a.platform.slug),
    })
  )

  const categoryEnablesPlatform = (cat: Category, slug: string) =>
    (cat.platforms ?? []).some(a => a.platform.slug === slug && a.enabled)

  const orphanedPlatforms = Array.from(enabledSlugs)
    .map(slug => {
      const platform = (product.platforms ?? []).find(a => a.platform.slug === slug)?.platform
      if (!platform) return null
      const blockedByCategories = !(product.categories ?? []).some(c =>
        categoryEnablesPlatform(c, slug)
      )
      return blockedByCategories ? platform : null
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)

  const categoryNames = (product.categories ?? []).map(c => c.name).join(', ')
  const hasNoCategories = (product.categories ?? []).length === 0

  const allCategories: Category[] = categoriesData?.categories ?? []
  const assignedCategoryIds = new Set<number>(
    (product.categories ?? []).map((c: Category) => c.id),
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={`/products/${product.id}`}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Edit Product</h1>
          <p className="text-sm text-gray-500">Update product information</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Product Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div>
                <label htmlFor="kind" className="block text-sm font-medium text-gray-700">
                  Product Type *
                </label>
                <select
                  id="kind"
                  value={formData.kind}
                  onChange={(e) => handleInputChange('kind', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="BASE">🍽️ Base Product</option>
                  <option value="MODIFIER">➕ Modifier</option>
                  <option value="BUNDLE">📦 Bundle</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {formData.kind === 'BASE' && 'Main menu items that customers can order'}
                  {formData.kind === 'MODIFIER' && 'Add-ons or customizations for base products'}
                  {formData.kind === 'BUNDLE' && 'Combination of multiple products sold together'}
                </p>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter product description"
                />
              </div>

              <div>
                <label htmlFor="image_url" className="block text-sm font-medium text-gray-700">
                  Image URL
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="url"
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => handleInputChange('image_url', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                  {formData.image_url && (
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="h-12 w-12 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Optional: URL to a product image
                </p>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => handleInputChange('active', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_pos"
                    checked={formData.display_in_pos}
                    onChange={(e) => handleInputChange('display_in_pos', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_pos" className="ml-2 block text-sm text-gray-700">
                    Display in POS
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_webshop"
                    checked={formData.display_in_webshop}
                    onChange={(e) => handleInputChange('display_in_webshop', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_webshop" className="ml-2 block text-sm text-gray-700">
                    Display in Webshop
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Platforms */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Available on delivery platforms</h2>
            <p className="text-sm text-gray-500 mb-4">
              Only products marked here are pushed to that platform's catalog. Requires the product's category to also be enabled.
            </p>
            <PlatformPicker
              availabilities={availabilities}
              onChange={handlePlatformChange}
            />
            {hasNoCategories && enabledSlugs.size > 0 && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                This product has no categories assigned, so it can't be pushed to delivery platforms. Assign it to a delivery-enabled category first.
              </p>
            )}
            {!hasNoCategories && orphanedPlatforms.length > 0 && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                This product won't appear on{' '}
                {orphanedPlatforms.map(p => p.displayName).join(', ')} — none of its
                categories ({categoryNames}) have{' '}
                {orphanedPlatforms.length === 1 ? 'that platform' : 'those platforms'}{' '}
                enabled.
              </p>
            )}
          </div>

          {/* Categories */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Categories</h2>
            <p className="text-sm text-gray-500 mb-4">
              Assign this product to one or more categories. Changes save immediately.
            </p>
            <CategoryAssignmentPicker
              allCategories={allCategories}
              assignedCategoryIds={assignedCategoryIds}
              inFlightCategoryIds={inFlightCategoryIds}
              onChange={handleCategoryChange}
            />
          </div>

          {/* Allergens & Attributes */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Allergens & Attributes</h2>
            <p className="text-sm text-gray-500 mb-4">
              Display metadata shown in the kiosk and webshop. Saved with the rest of the product.
            </p>
            <AllergenAttributeEditor value={labels} onChange={setLabels} />
          </div>

          {/* Current Product Info */}
          <div className="card bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Current Product Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Variants:</span>
                <span className="ml-2 text-gray-900">{product.variants.length}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Categories:</span>
                <span className="ml-2 text-gray-900">{product.categories.length}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-900">
                  {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Last Updated:</span>
                <span className="ml-2 text-gray-900">
                  {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <Link
              to={`/products/${product.id}`}
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={updateLoading || !formData.name?.trim()}
              className="btn-primary"
            >
              {updateLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Updating...
                </>
              ) : (
                'Update Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditProductPage
